import { nextReconnectDelay, resolveWebSocketCandidates } from './websocketConnectionPolicy.js'
import { WebSocketLifecycle, monotonicNow, visible } from './websocketLifecycle.js'
import { WebSocketHeartbeat } from './websocketHeartbeat.js'
import { WebSocketDiagnostics, socketId } from './websocketDiagnostics.js'
import { WebSocketResync } from './websocketResync.js'

export class WebSocketService {
    constructor() {
        this.ws = null
        this.listeners = new Map()
        this.shouldReconnect = true
        this.isConnecting = false
        this.ready = false
        this.state = 'disconnected'
        this.generation = 0
        this.snapshotRevision = 0
        this.candidateUrls = []
        this.currentCandidateIndex = 0
        this.currentBackoff = 1000
        this.reconnectAttempts = 0
        this.reconnectTimeoutId = null
        this.serverIdentity = {}
        this.lifecycle = new WebSocketLifecycle(this)
        this.heartbeat = new WebSocketHeartbeat(this)
        this.diagnostics = new WebSocketDiagnostics(this)
        this.resync = new WebSocketResync(this)
    }
    connect() {
        if (!this.shouldReconnect) {
            globalThis.clientLogger?.info('WS connect skipped: user not authenticated')
            return
        }
        this.lifecycle.attach()
        if (!this.lifecycle.available() || this.isConnecting || this.isConnected()) return
        this.clearReconnect()
        try {
            if (!this.candidateUrls.length) this.candidateUrls = resolveWebSocketCandidates().urls
            this.connectionId = socketId()
            this.serverIdentity = {}
            this.heartbeat.reset()
            this.connectStarted = monotonicNow()
            this.isConnecting = true
            this.ready = false
            this.state = 'connecting'
            const socket = new WebSocket(this.candidateUrls[this.currentCandidateIndex])
            this.ws = socket
            this.startHeartbeat()
            socket.onopen = () => {
                if (this.ws !== socket) return
                this.isConnecting = false
                this.state = 'checking'
                this.generation += 1
                this.heartbeat.lastIncoming = monotonicNow()
                this.heartbeat.probe()
                this.emit('connected')
            }
            socket.onmessage = event => {
                if (this.ws !== socket) return
                try { this.handleMessage(JSON.parse(event.data)) }
                catch { this.diagnostics.failed('invalid_message') }
            }
            socket.onclose = event => {
                if (this.ws !== socket) return
                this.clearSocket()
                this.handleSocketClose(event)
            }
            socket.onerror = () => {
                if (this.ws === socket) this.emit('error', { category: 'transport_error' })
            }
        } catch {
            this.clearSocket()
            this.diagnostics.failed('connect_failed')
            this.advanceCandidate()
            this.scheduleReconnect()
        }
    }
    clearSocket() {
        this.generation++
        this.resync.invalidate()
        this.ws = null
        this.isConnecting = false
        this.ready = false
        this.state = 'disconnected'
        this.stopHeartbeat()
        this.emit('disconnected')
    }
    retire(cause, { reconnect = true, expected = false } = {}) {
        const socket = this.ws
        if (expected) this.diagnostics.lifecycle(cause)
        else this.diagnostics.failed(cause, { initiator: 'client' })
        // Retire immediately: a missing close event must not block a new generation.
        this.clearSocket()
        if (socket) {
            socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null
            try { socket.close(expected ? 1000 : 4000, cause) } catch { /* Already closed. */ }
        }
        if (reconnect) this.scheduleReconnect()
    }
    pause(cause) {
        this.clearReconnect()
        if (this.ws) this.retire(cause, { reconnect: false, expected: true })
        this.state = 'suspended'
    }
    resume(reason) {
        if (!this.shouldReconnect || !this.lifecycle.available()) return
        this.clearReconnect()
        if (this.isConnecting) { this.connectStarted = monotonicNow(); return }
        if (this.isConnected()) {
            if (this.state === 'checking' && this.heartbeat.pending &&
                monotonicNow() - this.heartbeat.pending.sent < 1000 && reason !== 'timer_gap') return
            if (this.state !== 'checking') {
                this.ready = false
                this.generation += 1
                this.state = 'checking'
                this.emit('checking')
                this.resync.invalidate()
            }
            this.heartbeat.resume()
        } else this.connect()
    }
    confirmReady() {
        if (this.ready) return
        this.ready = true
        this.state = 'connected'
        this.diagnostics.recovered()
        this.currentBackoff = 1000
        this.reconnectAttempts = 0
        this.emit('ready', { generation: this.generation, connectionId: this.connectionId })
        void this.resync.run()
    }
    handleMessage(data) {
        if (!data || typeof data.type !== 'string') return
        if (this.heartbeat.receive(data)) return
        this.snapshotRevision++
        if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('corecry:bot-ws-event', { detail: data }))
        this.emit(data.type, data)
        this.emit('message', data)
    }
    handleSocketClose(event = {}) {
        const code = event.code
        const unauthorized = code === 4401 || event.reason?.trim().toLowerCase() === 'no session'
        const cause = unauthorized ? 'unauthorized' : ({ 1013: event.reason === 'message limit' ? 'message_limit' : 'server_policy_limit', 1011: 'server_error',
            1000: 'peer_closed', 1001: 'server_going_away', 1012: 'server_restart' }[code] || 'unknown_abnormal')
        this.diagnostics.failed(cause, { close_code: code, was_clean: Boolean(event.wasClean), initiator: 'remote_or_transport' })
        if (unauthorized) {
            this.clearReconnect()
            this.shouldReconnect = false
            this.lifecycle.detach()
            globalThis.clientLogger?.warn('WS unauthorized close: no session')
            this.emit('unauthorized', { code, reason: event.reason, message: event.reason || 'Unauthorized WebSocket session' })
            return
        }
        if (code === 1013) this.currentBackoff = 15000
        this.advanceCandidate()
        this.scheduleReconnect()
    }
    scheduleReconnect() {
        if (!this.shouldReconnect || !this.lifecycle.available() || this.reconnectTimeoutId) return
        let delay = this.calculateBackoffDelay()
        if (!visible()) delay = Math.min(60000, Math.max(30000, delay * 4))
        this.reconnectAttempts += 1
        this.state = 'reconnecting'
        this.emit('connection_failed', { attempts: this.reconnectAttempts })
        this.reconnectTimeoutId = setTimeout(() => {
            this.reconnectTimeoutId = null
            this.connect()
        }, delay)
    }
    clearReconnect() { clearTimeout(this.reconnectTimeoutId); this.reconnectTimeoutId = null }
    calculateBackoffDelay() {
        const { delay, nextBackoff } = nextReconnectDelay(this.currentBackoff)
        this.currentBackoff = nextBackoff
        return delay
    }
    advanceCandidate() {
        this.currentCandidateIndex = (this.currentCandidateIndex + 1) % (this.candidateUrls.length || 1)
    }
    disconnect() {
        this.resync.invalidate()
        this.resync.resetWarnings()
        this.shouldReconnect = false
        this.clearReconnect()
        if (this.ws) this.retire('logout', { reconnect: false, expected: true })
        this.stopHeartbeat()
        this.lifecycle.detach()
        this.diagnostics.incident = null
        globalThis.clientLogger?.resetSession?.()
    }
    allowReconnect() { this.shouldReconnect = true; this.lifecycle.attach() }
    startHeartbeat() { this.heartbeat.start() }
    stopHeartbeat() { this.heartbeat.stop() }
    isConnected() { return Boolean(this.ws && this.ws.readyState === WebSocket.OPEN) }
    send(data) {
        if (!this.isConnected()) return
        try { this.ws.send(JSON.stringify(data)) } catch { this.retire('send_failed') }
    }
    on(event, callback) {
        if (!this.listeners.has(event)) this.listeners.set(event, new Set())
        this.listeners.get(event).add(callback)
    }
    off(event, callback) { this.listeners.get(event)?.delete(callback) }
    emit(event, data) {
        this.listeners.get(event)?.forEach(callback => {
            try { callback(data) } catch { this.diagnostics.failed('listener_failed') }
        })
    }
}

export default new WebSocketService()
